import React from 'react';

export const Icon = ({ className = 'w-4 h-4', children }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    {children}
  </svg>
);

export function Figure2Dvs4D() {
  const bars = [45, 60, 38, 72, 55, 88, 64, 48, 58, 42];
  const pins = [
    { top: '22%', left: '30%', danger: false },
    { top: '58%', left: '64%', danger: true },
    { top: '74%', left: '22%', danger: false },
  ];

  return (
    <figure className="mt-8 border border-gray-200 rounded-xl overflow-hidden bg-gray-50">
      <div className="grid grid-cols-1 sm:grid-cols-2">
        <div className="p-5">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gray-400 mb-4">
            <span className="text-gray-700 font-semibold">Today</span> · 2D dashboard
          </p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="border border-gray-200 rounded-md bg-white p-2">
              <small className="block font-mono text-[9px] uppercase tracking-widest text-gray-400">Footfall</small>
              <b className="text-sm text-gray-800">8,412</b>
            </div>
            <div className="border border-red-200 rounded-md bg-red-50 p-2">
              <small className="block font-mono text-[9px] uppercase tracking-widest text-gray-400">Conv.</small>
              <b className="text-sm text-red-600">-12%</b>
            </div>
            <div className="border border-gray-200 rounded-md bg-white p-2">
              <small className="block font-mono text-[9px] uppercase tracking-widest text-gray-400">Dwell</small>
              <b className="text-sm text-gray-800">4m 20s</b>
            </div>
          </div>
          <div className="flex items-end gap-1.5 h-14">
            {bars.map((h, i) => (
              <div
                key={i}
                className={`flex-1 rounded-t-sm ${h === 88 ? 'bg-red-400' : 'bg-gray-200'}`}
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>

        <div className="p-5 border-t sm:border-t-0 sm:border-l border-gray-200">
          <p className="font-mono text-[11px] uppercase tracking-widest text-gray-400 mb-4">
            <span className="text-gray-700 font-semibold">The shift</span> · 4D space + time
          </p>
          <div className="flex justify-center items-center h-40 overflow-hidden">
            <div
              className="relative w-48 h-32 border border-gray-200 rounded bg-white"
              style={{
                transform: 'rotateX(55deg) rotateZ(-38deg)',
                backgroundImage:
                  'linear-gradient(rgba(0,0,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.06) 1px, transparent 1px)',
                backgroundSize: '20px 20px',
              }}
            >
              {pins.map((pin, i) => (
                <span key={i} className="absolute w-2.5 h-2.5" style={{ top: pin.top, left: pin.left }}>
                  <span
                    className={`absolute inset-0 rounded-full animate-ping ${
                      pin.danger ? 'bg-red-400' : 'bg-gray-400'
                    }`}
                  />
                  <span
                    className={`relative block w-2.5 h-2.5 rounded-full ${
                      pin.danger ? 'bg-red-500' : 'bg-gray-600'
                    }`}
                  />
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      <figcaption className="border-t border-gray-200 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-gray-400">
        Left: the reader reconstructs the story. Right: the story is the interface.
      </figcaption>
    </figure>
  );
}

export function ConvergenceDiagram() {
  const nodes = [
    {
      label: 'Operational data',
      icon: (
        <Icon className="w-5 h-5 mx-auto mb-1.5 text-gray-800">
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M3 5V19A9 3 0 0 0 21 19V5" />
          <path d="M3 12A9 3 0 0 0 21 12" />
        </Icon>
      ),
    },
    {
      label: 'World model',
      icon: (
        <Icon className="w-5 h-5 mx-auto mb-1.5 text-gray-800">
          <path d="M12 2a4 4 0 0 1 4 4c0 1.95-1.4 3.58-3.25 3.93L12 22" />
          <path d="M8 6a4 4 0 0 1 8 0" />
        </Icon>
      ),
    },
    {
      label: 'Browser rendering',
      icon: (
        <Icon className="w-5 h-5 mx-auto mb-1.5 text-gray-800">
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </Icon>
      ),
    },
  ];

  return (
    <div className="my-8 border border-gray-200 rounded-xl bg-gray-50 p-6 flex flex-col items-center">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
        {nodes.map((n) => (
          <div
            key={n.label}
            className="border border-gray-200 rounded-lg bg-white p-3 text-center font-mono text-[11px] uppercase tracking-wider text-gray-500"
          >
            {n.icon}
            {n.label}
          </div>
        ))}
      </div>
      <div className="w-0.5 h-7 bg-gray-300 my-2" />
      <div className="border border-gray-400 bg-gray-100 rounded-full px-6 py-2.5 font-mono text-[11px] uppercase tracking-widest text-gray-800 font-semibold">
        Grounded, replayable operations
      </div>
    </div>
  );
}

export function StatBand() {
  const stats = [
    { value: '30 yrs', label: 'Blocked by the stack' },
    { value: '30 mo', label: 'To break the bottlenecks' },
    { value: '4D', label: 'The new default' },
  ];
  return (
    <div className="mt-8 border border-gray-200 rounded-xl bg-gray-50 grid grid-cols-1 sm:grid-cols-3 overflow-hidden">
      {stats.map((s, i) => (
        <div
          key={s.label}
          className={`p-6 text-center ${
            i > 0 ? 'border-t sm:border-t-0 sm:border-l border-gray-200' : ''
          }`}
        >
          <b className="block text-3xl font-black tracking-tight text-gray-900">{s.value}</b>
          <small className="block mt-1 font-mono text-[10px] uppercase tracking-widest text-gray-400">
            {s.label}
          </small>
        </div>
      ))}
    </div>
  );
}

export function PullQuote({ children }) {
  return (
    <blockquote className="my-10 border-l-2 border-gray-400 bg-gray-50 rounded-r-xl px-6 py-5 text-lg italic leading-relaxed text-gray-800">
      {children}
    </blockquote>
  );
}

export function Point({ icon, title, children }) {
  return (
    <div className="border-l-2 border-gray-200 hover:border-gray-500 transition-colors pl-5 py-1">
      <div className="flex items-center gap-2.5 mb-1.5">
        <span className="text-gray-500 shrink-0">{icon}</span>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      </div>
      <p className="text-sm text-gray-600 leading-relaxed">{children}</p>
    </div>
  );
}
